import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Info } from '@phosphor-icons/react';

/**
 * PayPal Pay Later (4x sans frais) — simulator widget.
 *
 * Props:
 *   amount  : Total amount in EUR
 *   store   : Store object (with .settings for paypal_pay_later_enabled / _min)
 *   variant : 'inline' (compact, for product card) | 'block' (full block, for checkout)
 */
const PaypalPayLater = ({ amount, store, variant = 'inline' }) => {
  const [openDetails, setOpenDetails] = useState(false);

  const clientId = store?.settings?.paypal_client_id;
  // Load PayPal messaging SDK (idempotent) — hook MUST be called unconditionally
  useEffect(() => {
    if (!clientId) return;
    const id = `pp-sdk-msg-${clientId}`;
    if (document.getElementById(id)) return;
    const s = document.createElement('script');
    s.id = id;
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&components=messages&buyer-country=FR&currency=EUR`;
    s.async = true;
    document.body.appendChild(s);
  }, [clientId]);

  if (!amount || !store?.settings) return null;
  const enabled = !!store.settings.enable_paypal && !!store.settings.paypal_pay_later_enabled;
  const min = parseFloat(store.settings.paypal_pay_later_min || 600);
  if (!enabled || amount < min) return null;

  const installments = 4;
  const monthly = Math.round((amount / installments) * 100) / 100;

  const fmt = (n) => n.toFixed(2).replace('.', ',') + ' €';

  if (variant === 'inline') {
    return (
      <button
        onClick={(e) => { e.preventDefault(); setOpenDetails(true); }}
        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#003087] bg-[#FFC439]/20 hover:bg-[#FFC439]/30 px-2 py-1 rounded-md transition-colors"
        data-testid="paypal-paylater-badge"
      >
        <span className="font-black text-[#003087]">Pay<span className="italic">Pal</span></span>
        <span>4× sans frais — {fmt(monthly)}/mois</span>
        <Info size={12} />
        <PayLaterDialog open={openDetails} setOpen={setOpenDetails} amount={amount} monthly={monthly} clientId={clientId} />
      </button>
    );
  }

  // 'block' variant (for checkout)
  return (
    <div className="relative overflow-hidden rounded-xl border-2 border-[#003087]/20 bg-gradient-to-br from-[#FFC439]/10 via-white to-[#003087]/5 p-4 my-3" data-testid="paypal-paylater-block">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#003087] text-white flex items-center justify-center font-black text-[14px]">
          4×
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-bold text-slate-900 mb-0.5">
            Payez en 4× sans frais avec <span className="text-[#003087]">PayPal</span>
          </p>
          <p className="text-[13px] text-slate-600">
            4 mensualités de <strong className="text-slate-900">{fmt(monthly)}</strong> · 0 % de frais
          </p>
          <button
            onClick={() => setOpenDetails(true)}
            className="text-[12px] text-[#003087] hover:underline font-medium mt-1"
            data-testid="paypal-paylater-details"
          >
            Voir le détail de l'échéancier →
          </button>
        </div>
      </div>
      {/* Native PayPal messaging widget (if SDK loaded) */}
      {clientId && (
        <div
          data-pp-message
          data-pp-amount={amount.toFixed(2)}
          data-pp-style-layout="text"
          data-pp-style-logo-type="inline"
          data-pp-style-text-color="black"
          className="mt-2"
        />
      )}
      <PayLaterDialog open={openDetails} setOpen={setOpenDetails} amount={amount} monthly={monthly} clientId={clientId} />
    </div>
  );
};

const PayLaterDialog = ({ open, setOpen, amount, monthly, clientId }) => {
  const fmt = (n) => n.toFixed(2).replace('.', ',') + ' €';
  const today = new Date();
  const schedule = [0, 30, 60, 90].map((days, idx) => {
    const d = new Date(today); d.setDate(d.getDate() + days);
    return {
      idx: idx + 1,
      label: idx === 0 ? "Aujourd'hui" : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' }),
      amount: monthly,
    };
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-[#003087] font-black">Pay<span className="italic">Pal</span></span>
            <span className="text-slate-700">— 4× sans frais</span>
          </DialogTitle>
          <DialogDescription>Échéancier prévisionnel pour {fmt(amount)} TTC</DialogDescription>
        </DialogHeader>
        <div className="space-y-3" data-testid="paylater-schedule">
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
            {schedule.map(s => (
              <div key={s.idx} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-[13px] font-semibold text-slate-900">Mensualité {s.idx}/4</p>
                  <p className="text-[12px] text-slate-500">{s.label}</p>
                </div>
                <span className="text-[15px] font-bold text-[#003087]">{fmt(s.amount)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50">
              <span className="text-[13px] font-semibold text-slate-700">Total</span>
              <span className="text-[15px] font-black text-slate-900">{fmt(amount)} · 0 % frais</span>
            </div>
          </div>
          <p className="text-[11px] text-slate-500">
            Échéancier indicatif. L'éligibilité finale et le calendrier exact sont confirmés par PayPal au moment du paiement (vérification soft de votre profil, sans impact sur votre score crédit). Réservé aux résidents français majeurs.
          </p>
          {!clientId && (
            <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
              ⚠ PayPal n'est pas configuré pour ce magasin. Le paiement 4× ne sera pas disponible tant que l'administrateur n'aura pas saisi ses clés API.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaypalPayLater;
